'use client';
import React, { useState, useEffect } from 'react';
import { Venue } from '../../../../shared/models';
import { Box, Typography, Autocomplete, TextField, Chip } from '@mui/material';

interface SimilarVenueSelectProps {
    currentVenue: Venue;
    allVenues: Venue[];
    onSimilarVenuesChange: (selectedVenueIds: string[]) => void;
}

const SimilarVenueSelect: React.FC<SimilarVenueSelectProps> = ({
    currentVenue,
    allVenues,
    onSimilarVenuesChange
}) => {
    const locationTypes = [
        'greenwich village', 'midtown', 'east village', 'queens', 'soho',
        'williamsburg', 'brooklyn', 'lower east side', 'lower manhattan',
    ];

    // Filter out current venue and disabled venues
    const availableVenues = allVenues.filter(venue =>
        venue.ID !== currentVenue.ID && venue.Enabled
    );

    // Find venues with similar locations
    const recommendedVenues = availableVenues.filter(venue => {
        const currentLocationKeys = Object.keys(currentVenue.Attributes || {})
            .filter(key => locationTypes.includes(key.toLowerCase()));
        const venueLocationKeys = Object.keys(venue.Attributes || {})
            .filter(key => locationTypes.includes(key.toLowerCase()));

        return currentLocationKeys.some(currentKey =>
            venueLocationKeys.some(venueKey =>
                currentKey.toLowerCase() === venueKey.toLowerCase() &&
                currentVenue.Attributes?.[currentKey] &&
                venue.Attributes?.[venueKey]
            )
        );
    });

    // Initialize selected venues from currentVenue.SimilarVenueIds if it exists
    const [selectedVenues, setSelectedVenues] = useState<Venue[]>(() => {
        const similarIds = currentVenue.SimilarVenueIds || [];
        return availableVenues.filter(venue => similarIds.includes(venue.ID));
    });

    const handleChange = (_event: any, newValue: Venue[]) => {
        setSelectedVenues(newValue);
        onSimilarVenuesChange(newValue.map(venue => venue.ID));
    };

    return (
        <Box sx={{
            p: 3,
            backgroundColor: '#1a2b4d',
            borderRadius: 2,
            mb: 2
        }}>
            <Typography
                variant="h6"
                sx={{
                    color: 'white',
                    mb: 2
                }}
            >
                Similar Venues
            </Typography>

            <Autocomplete
                multiple
                id="similar-venues-select"
                options={availableVenues}
                value={selectedVenues}
                onChange={handleChange}
                getOptionLabel={(option) => option.Name}
                groupBy={(option) =>
                    recommendedVenues.find(v => v.ID === option.ID)
                        ? "Recommended Similar Venues"
                        : "Other Venues"
                }
                renderGroup={(params) => (
                    <Box key={params.key}>
                        <Typography
                            sx={{
                                color: '#a0aec0',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                px: 2,
                                py: 1
                            }}
                        >
                            {params.group}
                        </Typography>
                        {params.children}
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select similar venues"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#2a3b5d',
                                '& fieldset': {
                                    borderColor: '#4a5568',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#718096',
                                },
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                }
                            },
                            '& .MuiInputLabel-root': {
                                color: '#a0aec0',
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <Box
                        component="li"
                        {...props}
                        key={option.ID}
                        sx={{
                            color: recommendedVenues.find(v => v.ID === option.ID)
                                ? 'black'
                                : '#a0aec0',
                            '&:hover': {
                                backgroundColor: '#2a3b5d',
                            }
                        }}
                    >
                        {option.Name}
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        return (
                            <Chip
                                {...tagProps}
                                key={option.ID}
                                label={option.Name}
                                sx={{
                                    backgroundColor: recommendedVenues.find(v => v.ID === option.ID)
                                        ? '#4a90e2'
                                        : '#718096',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': {
                                        color: 'white',
                                        '&:hover': {
                                            color: '#ff4444',
                                        }
                                    }
                                }}
                            />
                        );
                    })
                }
            />
        </Box>
    );
};

export default SimilarVenueSelect;
